import React, { useState, useEffect } from 'react';
import { Trash, Save, Plus, X } from 'lucide-react';
import { saveProviderSecret, deleteProviderSecret, fetchProviderSecrets } from '../../utils/api';
import Breadcrumbs from '../Breadcrumbs';
import { toast } from 'react-toastify';
import { Checkbox } from '@nextui-org/react';
import { TOAST_STYLE_SUCCESS } from '../../utils/constants';

const SecretsManager = () => {
  const [openAIKey, setOpenAIKey] = useState('');
  const [openAIOrg, setOpenAIOrg] = useState('');
  const [claudeKey, setClaudeKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [nebiusKey, setNebiusKey] = useState('');
  const [azureKeys, setAzureKeys] = useState([]);
  const [customProviders, setCustomProviders] = useState([]);
  const [isUseAllModels, setIsUseAllModels] = useState(true);

  useEffect(() => {
    fetchSecrets();
  }, []);

  const fetchSecrets = async () => {
    const secrets = await fetchProviderSecrets();
    setClaudeKey('');
    setGeminiKey('');
    setOpenAIKey('');
    setOpenAIOrg('');
    setAzureKeys([]);
    setCustomProviders([]);
    let newAzureKeys = [];
    let newCustomProviders = [];

    secrets.forEach((secret, i) => {
      console.log(secret);
      if (secret.type === 'openai') {
        setOpenAIKey(secret.key);
        setOpenAIOrg(secret.organization);
      } else if (secret.type === 'azure') {
        newAzureKeys.push({ realm: secret.realm, key: secret.key, url: secret.url, id: secret.id });
      } else if (secret.type === 'claude') {
        setClaudeKey(secret.key);
      } else if (secret.type === 'gemini') {
        setGeminiKey(secret.key);
      } else if (secret.type === 'nebius') {
        setNebiusKey(secret.key);
      } else if (secret.type.startsWith('custom-')) {
        newCustomProviders.push({
          id: secret.id || Date.now() + i,
          name: secret.type,
          key: secret.key,
          url: secret.url,
          models: Array.isArray(secret.models) ? secret.models.map(m => m.substring(m.indexOf('/') + 1)) : (secret.models ? secret.models.split(',').map(m => m.trim()) : [])
        });
      }
    });
    
    setAzureKeys(newAzureKeys);
    setCustomProviders(newCustomProviders);
  };

  const addAzureKey = () => {
    const newKey = { realm: '', key: '', uri: '' };
    setAzureKeys([...azureKeys, newKey]); 
  };

  const updateAzureKey = (index, field, value) => {
    const updatedKeys = azureKeys.map((key, i) => {
      if (i === index) {
        return { ...key, [field]: value };
      }
      return key;
    });
    setAzureKeys(updatedKeys);
  };

  const saveSecrets = async (type, index) => {
    if (type === 'openai') {
      await saveProviderSecret({
        type: 'openai',
        key: openAIKey,
        organization: openAIOrg
      });
    } else if (type === 'azure') {
      await saveProviderSecret({
        type: 'azure',
        key: azureKeys[index].key,
        realm: azureKeys[index].realm,
        url: azureKeys[index].uri
      });
    }
    else if (type === 'claude') {
      await saveProviderSecret({
        type: 'claude',
        key: claudeKey
      });
    }
    else if (type === 'gemini') {
      await saveProviderSecret({
        type: 'gemini',
        key: geminiKey
      });
    }
    else if (type === 'nebius') {
      await saveProviderSecret({
        type: 'nebius',
        key: nebiusKey,
      });
    }
    await fetchSecrets();
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} key was saved!`, TOAST_STYLE_SUCCESS);
  };

  const deleteKeys = async (id, remove = false) => {
    if (remove === true) {
      setCustomProviders(customProviders.filter(provider => provider.name !== id));
    }
    else {
      await deleteProviderSecret(id);
      await fetchSecrets();
      toast.success(`${id ? id.charAt(0).toUpperCase() + id.slice(1) : 'Your'} key was deleted!`, TOAST_STYLE_SUCCESS);
    }
  };

  const addCustomProvider = () => {
    const newProvider = {
      id: Date.now(),
      name: 'custom-',
      key: '',
      url: '',
      models: [],
    };
    setCustomProviders([...customProviders, newProvider]);
  };

  const updateCustomProvider = (id, field, value) => {
    const updatedProviders = customProviders.map(provider => {
      if (provider.id === id) {
        let newValue = value;
        if (field === 'name') {
          // Remove any special characters except alphanumeric and hyphen
          newValue = value.replace(/[^a-zA-Z0-9-]/g, '');
          // Ensure it starts with 'custom-'
          if (!newValue.startsWith('custom-')) {
            newValue = 'custom-';
          }
        }
        return { ...provider, [field]: newValue };
      }
      return provider;
    });
    setCustomProviders(updatedProviders);
  };

  const addModelToProvider = (providerId) => {
    const updatedProviders = customProviders.map(provider => {
      if (provider.id === providerId) {
        return {
          ...provider,
          models: [...provider.models, '']
        };
      }
      return provider;
    });
    setCustomProviders(updatedProviders);
  };

  const updateProviderModel = (providerId, modelIndex, value) => {
    const updatedProviders = customProviders.map(provider => {
      if (provider.id === providerId) {
        const updatedModels = [...provider.models];
        updatedModels[modelIndex] = value;
        return {
          ...provider,
          models: updatedModels
        };
      }
      return provider;
    });
    setCustomProviders(updatedProviders);
  };

  const removeProviderModel = (providerId, modelIndex) => {
    const updatedProviders = customProviders.map(provider => {
      if (provider.id === providerId) {
        const updatedModels = provider.models.filter((_, index) => index !== modelIndex);
        return {
          ...provider,
          models: updatedModels
        };
      }
      return provider;
    });
    setCustomProviders(updatedProviders);
  };

  const saveCustomProvider = async (provider) => {
    // Filter out empty model names
    const cleanModels = provider.models.filter(model => model.trim() !== '');
    
    await saveProviderSecret({
      type: provider.name,
      key: provider.key,
      url: provider.url,
      models: cleanModels.map(model => provider.name + "/" + model),
    });
    await fetchSecrets();
    toast.success(`Custom provider ${provider.name} was saved!`, TOAST_STYLE_SUCCESS);
  };

  const crumbs = [
    { path: '/', breadcrumb: 'Home' },
    { path: '/settings', breadcrumb: 'Settings' },
    { path: window.location.href, breadcrumb: 'Secrets Management' }
  ];

  return (
    <div className='p-8'>
      <Breadcrumbs crumbs={crumbs} />
      <div className='mt-8 mb-4'>
        <h2 className='text-lg font-semibold mb-2'>OpenAI:</h2>
        <div className='mb-2'>
          <label className='block text-sm font-medium text-gray-700'>API_KEY:</label>
          <input
            type='text'
            value={openAIKey}
            onChange={(e) => setOpenAIKey(e.target.value)}
            className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
          />
        </div>
        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-700'>ORG:</label>
          <input
            type='text'
            value={openAIOrg}
            onChange={(e) => setOpenAIOrg(e.target.value)}
            className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
          />
        </div>
        <div className='flex gap-2 justify-end'>
          <Save color='black' cursor='pointer' size={21} onClick={() => saveSecrets('openai', 0)} />
          {openAIKey === '****' && <Trash color='black' cursor='pointer' size={20} onClick={() => deleteKeys('openai')} />}
        </div>
      </div>
      <hr className='my-4' />
      <div className='mb-4'>
        <h2 className='text-lg font-semibold mb-2'>Azure:</h2>
        {azureKeys.map((key, index) => (
          <div key={index} className='mb-6'>
            <div className='mb-2'>
              <label className='block text-sm font-medium text-gray-700'>Realm:</label>
              <input
                type='text'
                value={key.realm}
                onChange={(e) => updateAzureKey(index, 'realm', e.target.value)}
                className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
              />
            </div>
            <div className='mb-2'>
              <label className='block text-sm font-medium text-gray-700'>Key:</label>
              <input
                type='text'
                value={key.key}
                onChange={(e) => updateAzureKey(index, 'key', e.target.value)}
                className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
              />
            </div>
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700'>URL:</label>
              <input
                type='text'
                value={key.url}
                onChange={(e) => updateAzureKey(index, 'uri', e.target.value)}
                className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
              />
            </div>
            <div className='flex gap-1 justify-end'>
              <Save cursor='pointer' size={21} onClick={() => saveSecrets('azure', index)} />
              {key.key === '****' && <Trash cursor='pointer' size={20} onClick={() => deleteKeys(key.id)} color='black' />}
            </div>
          </div>
        ))}
        <button
          onClick={addAzureKey}
          style={{ backgroundColor: '#000000', padding: '0.5rem 0.7rem', borderRadius: '0.4rem', color: 'white' }}
        >
          + Add
        </button>
      </div>
      <hr className='my-4' />
      <div className='mb-4'>
        <h2 className='text-lg font-semibold mb-2'>Claude:</h2>
        <div className='mb-2'>
          <label className='block text-sm font-medium text-gray-700'>API_KEY:</label>
          <input
            type='text'
            value={claudeKey}
            onChange={(e) => setClaudeKey(e.target.value)}
            className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
          />
        </div>
        <div className='flex gap-1 justify-end'>
          <Save cursor='pointer' size={21} onClick={() => saveSecrets('claude', 0)} />
          {claudeKey === '****' && <Trash cursor='pointer' size={20} onClick={() => deleteKeys('claude')} color='black' /> }
        </div>
      </div>
      <hr className='my-4' />
      <div className='mb-4'>
        <h2 className='text-lg font-semibold mb-2'>Gemini:</h2>
        <div className='mb-2'>
          <label className='block text-sm font-medium text-gray-700'>API_KEY:</label>
          <input
            type='text'
            value={geminiKey}
            onChange={(e) => setGeminiKey(e.target.value)}
            className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
          />
        </div>
        <div className='flex gap-1 justify-end'>
          <Save cursor='pointer' size={21} onClick={() => saveSecrets('gemini', 0)} />
          {geminiKey === '****' && <Trash cursor='pointer' size={20} onClick={() => deleteKeys('gemini')} color='black' />}
        </div>
      </div>
      <hr className='my-4' />
      <div className='mb-4'>
        <h2 className='text-lg font-semibold mb-2'>Nebius:</h2>
        <div className='mb-2'>
          <label className='block text-sm font-medium text-gray-700'>API_KEY:</label>
          <input
            type='text'
            value={nebiusKey}
            onChange={(e) => setNebiusKey(e.target.value)}
            className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
          />
        </div>
        <div className='flex gap-1 justify-end'>
          <Save cursor='pointer' size={21} onClick={() => saveSecrets('nebius', 0)}/>
          <Trash cursor='pointer' size={20} onClick={() => deleteKeys('nebius')} color='black' />
        </div>
      </div>
      <hr className='my-4' />
      <div className='mb-4'>
        <div className='flex justify-between items-center mb-4'>
          <h2 className='text-lg font-semibold'>Custom Providers:</h2>
          <button
            onClick={addCustomProvider}
            className='flex items-center gap-2 bg-black text-white px-3 py-2 rounded-md hover:bg-gray-800'
          >
            <Plus size={16} /> Add Custom Provider
          </button>
        </div>
        
        <div className='max-h-96 overflow-y-auto pr-2'>
          {customProviders.map((provider) => (
            <div key={provider.id} className='mb-6 p-4 border border-gray-200 rounded-lg'>
              <div className='flex justify-between items-start mb-4'>
                <div className='flex-1'>
                  <label className='block text-sm font-medium text-gray-700'>Provider Name:</label>
                  <input
                    type='text'
                    value={provider.name}
                    onChange={(e) => updateCustomProvider(provider.id, 'name', e.target.value)}
                    placeholder='custom'
                    className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
                  />
                </div>
              </div>
              
              <div className='mb-2'>
                <label className='block text-sm font-medium text-gray-700'>API Key:</label>
                <input
                  type='text'
                  value={provider.key}
                  onChange={(e) => updateCustomProvider(provider.id, 'key', e.target.value)}
                  className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
                />
              </div>
              
              <div className='mb-2'>
                <label className='block text-sm font-medium text-gray-700'>API URL:</label>
                <input
                  type='text'
                  value={provider.url}
                  onChange={(e) => updateCustomProvider(provider.id, 'url', e.target.value)}
                  className='mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
                />
              </div>
              
              <div className='mb-4'>
                <div className='flex justify-between items-center mb-2'>
                  <label className='block text-sm font-medium text-gray-700'>Available Models:</label>
                  <button
                    onClick={() => addModelToProvider(provider.id)}
                    className='flex items-center gap-1 text-sm text-black hover:text-gray-600'
                  >
                    <Plus size={16} /> Add Model
                  </button>
                </div>
                <div className='border border-gray-200 rounded-md'>
                  <div className='max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'>
                    <div className='p-2 space-y-2'>
                      {provider.models.map((model, modelIndex) => (
                        <div key={modelIndex} className='flex items-center gap-2 group'>
                          <input
                            type='text'
                            value={model}
                            onChange={(e) => updateProviderModel(provider.id, modelIndex, e.target.value)}
                            placeholder='Enter model name'
                            className='flex-1 p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white'
                          />
                          <button
                            onClick={() => removeProviderModel(provider.id, modelIndex)}
                            className='p-1 text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity'
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                      {provider.models.length === 0 && (
                        <div className='p-4 text-center'>
                          <p className='text-sm text-gray-500 italic'>No models added yet. Click "Add Model" to add one.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className='flex justify-end gap-2'>
                <button
                  onClick={() => saveCustomProvider(provider)}
                  className='flex items-center gap-2 text-black hover:text-gray-600'
                >
                  <Save size={21} />
                </button>
                  <button
                    onClick={() => deleteKeys(provider.name, provider.key !== '****')}
                    className='flex items-center gap-2 text-black hover:text-gray-600'
                >
                  <Trash size={20} />
                </button>
                </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SecretsManager;
